import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminAlertText,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminHeaderAction,
  AdminLinkAction,
  AdminMetadataText,
  AdminMetric,
  AdminMetricDelta,
  AdminPageTitle,
  AdminPanelTitle,
  AdminRowActionsButton,
  AdminSearchField,
  AdminStatusBadge,
  AdminSupportingText,
  AdminTabButton,
  AdminTableBody,
  AdminTableHeader,
  AdminTableMeta,
  AdminWidgetTitle,
  AdminListFilterDropdown,
  toAdminListFilterOptions,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
  ADMIN_SECTION_OFFSET_CLASS,
  ADMIN_METRIC_PANEL_CLASS,
} from '../shared/adminUx';

import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import useClickOutside from '../../../Hooks/useClickOutside';

const PAGE_SIZE = 25;

const CONTENT_TYPE_OPTIONS = ['All', 'Posts', 'Reviews', 'Messages', 'Business Profiles', 'Customer Profiles', 'Documents & Licences', 'Misconduct', 'System Flags'];
const STATUS_OPTIONS = ['All', 'Active', 'Draft', 'In Revision', 'Archived'];
const YES_NO_OPTIONS = ['All', 'Yes', 'No'];
const DATE_RANGE_OPTIONS = ['All', '7 days', '30 days', '90 days', 'Custom'];

const KPI_DEFAULT = {
  totalWorkflows: { value: 0, change: 0 },
  contentTypesCovered: { value: 0, change: 0 },
  activeWorkflows: { value: 0, change: 0 },
  draftInRevision: { value: 0, change: 0 },
  lastWorkflowUpdate: { value: null, change: 0 },
  workflowsWithCustomRouting: { value: 0, change: 0 },
  workflowsWithCustomEscalation: { value: 0, change: 0 },
  deprecatedArchived: { value: 0, change: 0 },
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDateOnly = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatMetric = (value) => {
  if (value === null || value === undefined) return '—';
  return value.toLocaleString();
};

const generateWorkflows = () => {
  const contentTypes = ['Posts', 'Reviews', 'Messages', 'Business Profiles', 'Customer Profiles', 'Documents & Licences', 'Misconduct', 'System Flags'];
  const statuses = ['Active', 'Draft', 'In Revision', 'Archived'];
  const severityHandlings = ['Level 1–4 standard', 'Level 1–2 auto-approve', 'Level 3–4 manual review', 'Custom severity mapping'];
  const admins = ['Sarah K.', 'Michael T.', 'Rachel M.', 'John S.', 'Emma W.'];

  const randomFrom = (array) => array[Math.floor(Math.random() * array.length)];
  const items = [];
  const now = new Date();

  for (let i = 1; i <= 150; i++) {
    const contentType = randomFrom(contentTypes);
    const status = randomFrom(statuses);
    const hasCustomRouting = Math.random() > 0.5;
    const hasCustomEscalation = Math.random() > 0.5;
    const updatedBy = randomFrom(admins);
    const adminId = Math.floor(Math.random() * 50) + 1;

    const lastUpdated = new Date(now);
    lastUpdated.setDate(lastUpdated.getDate() - Math.floor(Math.random() * 180));

    const workflowName = `${contentType} — ${status === 'Active' ? 'Standard' : status === 'Draft' ? 'Draft' : status === 'In Revision' ? 'Revised' : 'Archived'} Moderation Workflow`;

    items.push({
      id: `WF#${String(i).padStart(4, '0')}`,
      workflowName,
      contentType,
      status,
      defaultSeverityHandling: randomFrom(severityHandlings),
      customRouting: hasCustomRouting ? 'Yes' : 'No',
      customEscalation: hasCustomEscalation ? 'Yes' : 'No',
      lastUpdated: lastUpdated.toISOString(),
      updatedBy: `Admin#${adminId} — "${updatedBy}"`,
    });
  }

  return items;
};

const renderDelta = (change) => {
  if (change === null || change === undefined) return null;
  return (
    <div className="mt-1">
      <AdminMetricDelta value={change} />
    </div>
  );
};


const ModerationWorkflows = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [kpis, setKpis] = useState(KPI_DEFAULT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workflows, setWorkflows] = useState([]);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [filters, setFilters] = useState({
    contentType: 'All',
    status: 'All',
    hasCustomRouting: 'All',
    hasCustomEscalation: 'All',
    dateRange: 'All',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('lastUpdated');
  const [sortOrder, setSortOrder] = useState('desc');

  const [contentTypeOpen, setContentTypeOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [customRoutingOpen, setCustomRoutingOpen] = useState(false);
  const [customEscalationOpen, setCustomEscalationOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);

  const contentTypeRef = useRef(null);
  const statusRef = useRef(null);
  const customRoutingRef = useRef(null);
  const customEscalationRef = useRef(null);
  const dateRangeRef = useRef(null);

  useClickOutside(contentTypeRef, () => setContentTypeOpen(false));
  useClickOutside(statusRef, () => setStatusOpen(false));
  useClickOutside(customRoutingRef, () => setCustomRoutingOpen(false));
  useClickOutside(customEscalationRef, () => setCustomEscalationOpen(false));
  useClickOutside(dateRangeRef, () => setDateRangeOpen(false));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const fetchWorkflows = async () => {
      setLoading(true);
      setError(null);

      try {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const generated = generateWorkflows();

        setWorkflows(generated);
        
        const uniqueContentTypes = new Set(generated.map(w => w.contentType)).size;
        const lastUpdate = generated.length > 0 
          ? generated.reduce((latest, w) => {
              const wDate = new Date(w.lastUpdated);
              const lDate = new Date(latest);
              return wDate > lDate ? w.lastUpdated : latest;
            }, generated[0].lastUpdated)
          : null;

        setKpis({
          totalWorkflows: { value: generated.length, change: 2.5 },
          contentTypesCovered: { value: uniqueContentTypes, change: 0 },
          activeWorkflows: { value: generated.filter((w) => w.status === 'Active').length, change: 1.8 },
          draftInRevision: { value: generated.filter((w) => w.status === 'Draft' || w.status === 'In Revision').length, change: -0.5 },
          lastWorkflowUpdate: { value: lastUpdate, change: 0 },
          workflowsWithCustomRouting: { value: generated.filter((w) => w.customRouting === 'Yes').length, change: 3.2 },
          workflowsWithCustomEscalation: { value: generated.filter((w) => w.customEscalation === 'Yes').length, change: 2.1 },
          deprecatedArchived: { value: generated.filter((w) => w.status === 'Archived').length, change: 0.3 },
        });
      } catch (err) {
        setError('Failed to load workflows. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflows();
  }, []);

  const filteredWorkflows = useMemo(() => {
    let data = [...workflows];

    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase().trim();
      data = data.filter((workflow) => {
        const nameMatch = workflow.workflowName?.toLowerCase().includes(query);
        const contentTypeMatch = workflow.contentType?.toLowerCase().includes(query);
        return nameMatch || contentTypeMatch;
      });
    }

    if (filters.contentType !== 'All') {
      data = data.filter((workflow) => workflow.contentType === filters.contentType);
    }

    if (filters.status !== 'All') {
      data = data.filter((workflow) => workflow.status === filters.status);
    }

    if (filters.hasCustomRouting !== 'All') {
      const isYes = filters.hasCustomRouting === 'Yes';
      data = data.filter((workflow) => (workflow.customRouting === 'Yes') === isYes);
    }

    if (filters.hasCustomEscalation !== 'All') {
      const isYes = filters.hasCustomEscalation === 'Yes';
      data = data.filter((workflow) => (workflow.customEscalation === 'Yes') === isYes);
    }

    if (filters.dateRange !== 'All' && filters.dateRange !== 'Custom') {
      const days = parseInt(filters.dateRange, 10);
      const threshold = new Date();
      threshold.setDate(threshold.getDate() - days);
      data = data.filter((workflow) => new Date(workflow.lastUpdated) >= threshold);
    }

    data.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];
      
      if (sortField === 'lastUpdated') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else {
        aValue = (aValue || '').toString().toLowerCase();
        bValue = (bValue || '').toString().toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return data;
  }, [workflows, debouncedSearch, filters, sortField, sortOrder]);

  const paginatedWorkflows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredWorkflows.slice(start, start + PAGE_SIZE);
  }, [filteredWorkflows, currentPage]);

  const totalPages = Math.max(1, Math.ceil(filteredWorkflows.length / PAGE_SIZE));
  const rangeStart = filteredWorkflows.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredWorkflows.length);
  const showPagination = filteredWorkflows.length > PAGE_SIZE;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    return null;
  };

  const handleResetFilters = () => {
    setFilters({
      contentType: 'All',
      status: 'All',
      hasCustomRouting: 'All',
      hasCustomEscalation: 'All',
      dateRange: 'All',
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const renderHeader = () => (
    <div className="grid py-3 text-offblack border-b border-lightgray px-4 gap-3" style={{ gridTemplateColumns: 'minmax(250px, 2fr) minmax(150px, 1fr) minmax(100px, 0.8fr) minmax(180px, 1.2fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(150px, 1fr) minmax(180px, 1.2fr) minmax(80px, 0.6fr)', width: '100%' }}>
      <div className="flex items-center cursor-pointer" onClick={() => handleSort('workflowName')}>
        <AdminTableHeader>Workflow Name</AdminTableHeader>
        {renderSortIcon('workflowName')}
      </div>
      <div className="flex items-center cursor-pointer" onClick={() => handleSort('contentType')}>
        <AdminTableHeader>Content Type</AdminTableHeader>
        {renderSortIcon('contentType')}
      </div>
      <div className="flex items-center cursor-pointer" onClick={() => handleSort('status')}>
        <AdminTableHeader>Status</AdminTableHeader>
        {renderSortIcon('status')}
      </div>
      <div className="flex items-center cursor-pointer" onClick={() => handleSort('defaultSeverityHandling')}>
        <AdminTableHeader>Default Severity Handling</AdminTableHeader>
        {renderSortIcon('defaultSeverityHandling')}
      </div>
      <div className="flex items-center cursor-pointer" onClick={() => handleSort('customRouting')}>
        <AdminTableHeader>Custom Routing</AdminTableHeader>
        {renderSortIcon('customRouting')}
      </div>
      <div className="flex items-center cursor-pointer" onClick={() => handleSort('customEscalation')}>
        <AdminTableHeader>Custom Escalation</AdminTableHeader>
        {renderSortIcon('customEscalation')}
      </div>
      <div className="flex items-center cursor-pointer" onClick={() => handleSort('lastUpdated')}>
        <AdminTableHeader>Last Updated</AdminTableHeader>
        {renderSortIcon('lastUpdated')}
      </div>
      <div className="flex items-center cursor-pointer" onClick={() => handleSort('updatedBy')}>
        <AdminTableHeader>Updated By</AdminTableHeader>
        {renderSortIcon('updatedBy')}
      </div>
      <div className="flex items-center">
        <AdminTableHeader>Actions</AdminTableHeader>
      </div>
    </div>
  );

  const renderRow = (workflow, index) => (
    <div
      key={workflow.id || index}
      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-3 cursor-pointer"
      style={{ gridTemplateColumns: 'minmax(250px, 2fr) minmax(150px, 1fr) minmax(100px, 0.8fr) minmax(180px, 1.2fr) minmax(100px, 0.8fr) minmax(100px, 0.8fr) minmax(150px, 1fr) minmax(180px, 1.2fr) minmax(80px, 0.6fr)', width: '100%' }}
      onClick={() => navigate(`/admin/moderation/workflows/${workflow.id}`, { state: { from: location.pathname, workflow: workflow } })}
    >
      <div className="flex items-center min-w-0">
        <AdminTableBody emphasis className="truncate">{workflow.workflowName}</AdminTableBody>
      </div>
      <div className="flex items-center min-w-0">
        <AdminTableBody className="truncate">{workflow.contentType}</AdminTableBody>
      </div>
      <div className="flex items-center">
        <AdminStatusBadge status={workflow.status} label={workflow.status} />
      </div>
      <div className="flex items-center min-w-0">
        <AdminTableBody className="truncate">{workflow.defaultSeverityHandling}</AdminTableBody>
      </div>
      <div className="flex items-center">
        <AdminTableBody>{workflow.customRouting}</AdminTableBody>
      </div>
      <div className="flex items-center">
        <AdminTableBody>{workflow.customEscalation}</AdminTableBody>
      </div>
      <div className="flex items-center">
        <AdminTableMeta>{formatDate(workflow.lastUpdated)}</AdminTableMeta>
      </div>
      <div className="flex items-center min-w-0">
        <AdminTableMeta className="truncate">{workflow.updatedBy}</AdminTableMeta>
      </div>
      <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
        <AdminLinkAction
          onClick={() =>
            navigate(`/admin/moderation/workflows/${workflow.id}`, {
              state: { from: location.pathname, workflow },
            })
          }
        >
          View
        </AdminLinkAction>
      </div>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>Loading workflows...</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-96">
          <AdminSupportingText>{error}</AdminSupportingText>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Moderation Workflows</AdminPageTitle>

        {/* KPI Row - 8 KPIs */}
        <div className={`grid grid-cols-2 lg:grid-cols-4 gap-4 ${ADMIN_SECTION_OFFSET_CLASS}`}>
          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack mb-2">Total Workflows</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(kpis.totalWorkflows.value)}</AdminMetric>
            {renderDelta(kpis.totalWorkflows.change)}
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack mb-2">Content Types Covered</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(kpis.contentTypesCovered.value)}</AdminMetric>
            {renderDelta(kpis.contentTypesCovered.change)}
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack mb-2">Active Workflows</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(kpis.activeWorkflows.value)}</AdminMetric>
            {renderDelta(kpis.activeWorkflows.change)}
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack mb-2">Draft / In Revision</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(kpis.draftInRevision.value)}</AdminMetric>
            {renderDelta(kpis.draftInRevision.change)}
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack mb-2">Last Workflow Update</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{kpis.lastWorkflowUpdate.value ? formatDateOnly(kpis.lastWorkflowUpdate.value) : '—'}</AdminMetric>
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack mb-2">Workflows With Custom Routing</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(kpis.workflowsWithCustomRouting.value)}</AdminMetric>
            {renderDelta(kpis.workflowsWithCustomRouting.change)}
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack mb-2">Workflows With Custom Escalation</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(kpis.workflowsWithCustomEscalation.value)}</AdminMetric>
            {renderDelta(kpis.workflowsWithCustomEscalation.change)}
          </DashboardMainContainer>

          <DashboardMainContainer className={ADMIN_METRIC_PANEL_CLASS}>
            <AdminSupportingText className="text-offblack mb-2">Deprecated / Archived</AdminSupportingText>
            <AdminMetric size="large" className="text-offblack mb-2 block">{formatMetric(kpis.deprecatedArchived.value)}</AdminMetric>
            {renderDelta(kpis.deprecatedArchived.change)}
          </DashboardMainContainer>
        </div>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            {/* Search on Left */}
            <div className="w-full lg:max-w-[450px]">
              <AdminSearchField
                placeholder="Search Workflow Name, Content Type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Filters on Right */}
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Content Type"
                value={filters.contentType}
                options={toAdminListFilterOptions(CONTENT_TYPE_OPTIONS, 'All Content Types')}
                onChange={(value) => setFilters((prev) => ({ ...prev, contentType: value }))}
                menuRef={contentTypeRef}
                open={contentTypeOpen}
                setOpen={setContentTypeOpen}
              />
              <AdminListFilterDropdown
                label="Status"
                value={filters.status}
                options={toAdminListFilterOptions(STATUS_OPTIONS, 'All Statuses')}
                onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
                menuRef={statusRef}
                open={statusOpen}
                setOpen={setStatusOpen}
              />
              <AdminListFilterDropdown
                label="Custom Routing"
                value={filters.hasCustomRouting}
                options={toAdminListFilterOptions(YES_NO_OPTIONS, 'All Routing')}
                onChange={(value) => setFilters((prev) => ({ ...prev, hasCustomRouting: value }))}
                menuRef={customRoutingRef}
                open={customRoutingOpen}
                setOpen={setCustomRoutingOpen}
              />
              <AdminListFilterDropdown
                label="Custom Escalation"
                value={filters.hasCustomEscalation}
                options={toAdminListFilterOptions(YES_NO_OPTIONS, 'All Escalation')}
                onChange={(value) => setFilters((prev) => ({ ...prev, hasCustomEscalation: value }))}
                menuRef={customEscalationRef}
                open={customEscalationOpen}
                setOpen={setCustomEscalationOpen}
              />
              <AdminListFilterDropdown
                label="Date Range"
                value={filters.dateRange}
                options={toAdminListFilterOptions(DATE_RANGE_OPTIONS, 'All Date Ranges')}
                onChange={(value) => setFilters((prev) => ({ ...prev, dateRange: value }))}
                menuRef={dateRangeRef}
                open={dateRangeOpen}
                setOpen={setDateRangeOpen}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          {loading ? (
            <div className="py-10 text-center">
              <AdminSupportingText>
                Loading workflows...
              </AdminSupportingText>
            </div>
          ) : error ? (
            <div className="py-10 text-center">
              <AdminSupportingText>
                {error}
              </AdminSupportingText>
            </div>
          ) : filteredWorkflows.length === 0 ? (
            <div className="py-10 text-center">
              <AdminSupportingText>
                No workflows found matching your filters.
              </AdminSupportingText>
            </div>
          ) : (
            <>
              <div className="overflow-hidden">
                {renderHeader()}
                {paginatedWorkflows.map((workflow, index) => renderRow(workflow, index))}
              </div>

            </>
          )}
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={!loading && !error && showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={filteredWorkflows.length}
          currentPage={currentPage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default ModerationWorkflows;

